/**
 * Sentry Integration Test Component
 * 
 * Development utility component for testing Sentry error reporting integration.
 * Provides buttons to trigger various types of errors and verify that they
 * are properly captured and reported to Sentry.
 * 
 * Features:
 * - Test JavaScript errors and exceptions
 * - Test unhandled promise rejections
 * - Test custom error reporting with context
 * - Test user context integration
 * - Breadcrumb testing functionality
 * 
 * @module TabPicker.Components.SentryTest
 */

import { captureException, addBreadcrumb } from '../utils/sentry';

/**
 * Sentry Test Button Component
 * 
 * Renders test buttons for verifying Sentry integration in development.
 * Only visible when VITE_DEBUG_SENTRY_CONTROLS environment variable is set to 'true'
 * and the app is running in development mode.
 * 
 * @returns JSX element with test buttons or null when debug controls are disabled
 */
export default function SentryTestButton() {
    // Only show in development mode with specific environment variable enabled
    const isDebugEnabled = import.meta.env.DEV && import.meta.env.VITE_DEBUG_SENTRY_CONTROLS === 'true';
    
    if (!isDebugEnabled) {
        return null;
    }

    /**
     * Test basic JavaScript error reporting
     */
    const testError = () => {
        addBreadcrumb('User clicked test error button', 'user', 'info');
        throw new Error('Test error from SentryTestButton');
    };

    /**
     * Test unhandled promise rejection
     */
    const testAsyncError = async () => {
        addBreadcrumb('User clicked test async error button', 'user', 'info');
        
        // Create an unhandled promise rejection
        Promise.reject(new Error('Test async error from SentryTestButton'));
    };

    /**
     * Test custom error with context
     */
    const testCustomError = () => {
        addBreadcrumb('User clicked test custom error button', 'user', 'info');
        
        const error = new Error('Test custom error with context');
        captureException(error, {
            component: 'SentryTestButton',
            action: 'test-custom-error',
            metadata: {
                testType: 'custom-context',
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
            },
        });
    };

    /**
     * Test network error simulation
     */
    const testNetworkError = async () => {
        addBreadcrumb('User clicked test network error button', 'user', 'info');
        
        try {
            // Attempt to fetch from a URL that will cause a network error
            const response = await fetch('https://nonexistent-domain-that-will-fail.invalid/test');
            
            if (!response.ok) {
                const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
                throw error;
            }
        } catch (error) {
            captureException(error as Error, {
                component: 'SentryTestButton',
                action: 'test-network-error',
                metadata: {
                    testType: 'network-failure',
                    endpoint: 'https://nonexistent-domain-that-will-fail.invalid/test',
                    error: error instanceof Error ? error.message : 'Unknown error',
                },
            });
        }
    };

    /**
     * Test breadcrumb functionality
     */
    const testBreadcrumbs = () => {
        addBreadcrumb('Step 1: User initiated breadcrumb test', 'test', 'info');
        addBreadcrumb('Step 2: Adding multiple breadcrumbs', 'test', 'info', {
            action: 'breadcrumb-sequence',
            step: 2,
        });
        addBreadcrumb('Step 3: Warning level breadcrumb', 'test', 'warning', {
            action: 'breadcrumb-sequence',
            step: 3,
        });
        
        // Trigger error after breadcrumbs
        setTimeout(() => {
            throw new Error('Test error after breadcrumb sequence');
        }, 100);
    };

    return (
        <div className="fixed bottom-4 right-4 p-4 bg-red-50 border border-red-200 rounded-lg shadow-lg">
            <h3 className="text-sm font-semibold text-red-800 mb-2">
                Sentry Test Controls (Dev Only)
            </h3>
            <div className="space-y-2">
                <button
                    onClick={testError}
                    className="block w-full px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                >
                    Test Error
                </button>
                <button
                    onClick={testAsyncError}
                    className="block w-full px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                >
                    Test Async Error
                </button>
                <button
                    onClick={testCustomError}
                    className="block w-full px-3 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                >
                    Test Custom Error
                </button>
                <button
                    onClick={testNetworkError}
                    className="block w-full px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Test Network Error
                </button>
                <button
                    onClick={testBreadcrumbs}
                    className="block w-full px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600"
                >
                    Test Breadcrumbs
                </button>
            </div>
        </div>
    );
}